'use client';

import type { SubmissionResult } from '@morfi/backend-sdk-v2/client';
import type { StoreApi, UseBoundStore } from 'zustand';
import { persist } from 'zustand/middleware';
import { createTrackedStore } from '@/lib/dev/zustand-registry';
import { getActiveNetwork } from '@/lib/network/active';
import type { Network } from '@/lib/network/types';
import { logger } from '@/lib/telemetry/logger';
import { useTradeSettingsStore } from './trade-settings';
import { useTxSubmissionStore } from './tx-submission';

export interface OutstandingTrade {
  id: string;
  network: Network;
  status: 'processing' | 'unknown';
  unknownAt?: number;
  traceId?: string;
  signature?: string;
}

interface TradeSubmissionLockState {
  outstanding: OutstandingTrade[];
  swapPending: boolean;
  finish: (id: string) => void;
}

const AMBIGUOUS_CODES: ReadonlySet<string> = new Set([
  'TX_CONFIRM_TIMEOUT',
  'TX_TURNKEY_TIMEOUT',
  'TX_TURNKEY_FAILED',
  'TX_RELAY_TIMEOUT',
  'TX_BLOCKHASH_EXPIRED',
  'TX_MAX_RETRIES',
  'TX_UNKNOWN',
]);

export const useTradeSubmissionLockStore: UseBoundStore<StoreApi<TradeSubmissionLockState>> =
  createTrackedStore<TradeSubmissionLockState>('useTradeSubmissionLockStore')(
    persist(
      (set) => ({
        outstanding: [],
        swapPending: false,
        finish: (id: string): void => {
          set((state) => {
            if (!state.outstanding.some((trade) => trade.id === id)) return state;
            return {
              outstanding: state.outstanding.filter((trade) => trade.id !== id),
            };
          });
        },
      }),
      {
        name: 'morfi-outstanding-trades',
        // Keep known unresolved outcomes across reloads without changing SDK submission tracking.
        partialize: (
          state: TradeSubmissionLockState,
        ): Pick<TradeSubmissionLockState, 'outstanding'> => ({
          outstanding: state.outstanding
            .filter((trade) => trade.traceId !== undefined)
            .map((trade) => ({ ...trade, status: 'unknown' as const })),
        }),
      },
    ),
  );

export function isTradeSubmissionBlocked(exclusive: boolean = false): boolean {
  const { outstanding, swapPending } = useTradeSubmissionLockStore.getState();
  return (
    swapPending ||
    outstanding.some((trade) => trade.status === 'unknown') ||
    (outstanding.length > 0 && (exclusive || !useTradeSettingsStore.getState().allowRapidTrades))
  );
}

/** Reserve synchronously, before the SDK or React can yield to another press. */
export function submitGuardedTrade(run: () => Promise<SubmissionResult>): void {
  if (isTradeSubmissionBlocked()) return;
  const id = crypto.randomUUID();
  useTradeSubmissionLockStore.setState((state) => ({
    outstanding: [...state.outstanding, { id, network: getActiveNetwork(), status: 'processing' }],
  }));
  void settleTrade(id, run);
}

async function settleTrade(id: string, run: () => Promise<SubmissionResult>): Promise<void> {
  try {
    const result = await run();
    const entry = useTxSubmissionStore.getState().submissions[result.traceId];
    if (result.ok || entry?.stage === 'confirmed') {
      useTradeSubmissionLockStore.getState().finish(id);
    } else if (
      entry?.stage === 'cancelled' ||
      result.error.stage === 'cancelled' ||
      result.error.code === 'TX_CANCELLED' ||
      !AMBIGUOUS_CODES.has(result.error.code)
    ) {
      useTradeSubmissionLockStore.getState().finish(id);
    } else {
      markUnknown(id, result.traceId, entry?.signature, result.error.code);
    }
  } catch {
    logger.error('trade.submission_promise_rejected', { submissionId: id });
    // A rejected client promise is not proof that a transaction did not land.
    const trade = useTradeSubmissionLockStore
      .getState()
      .outstanding.find((entry) => entry.id === id);
    markUnknown(id, trade?.traceId, trade?.signature, 'promise_rejected');
  }
}

function markUnknown(
  id: string,
  traceId: string | undefined,
  signature: string | undefined,
  code: string,
): void {
  useTradeSubmissionLockStore.setState((state) => ({
    outstanding: state.outstanding.map((trade) =>
      trade.id === id
        ? {
            ...trade,
            status: 'unknown',
            unknownAt: Date.now(),
            ...(traceId === undefined ? {} : { traceId }),
            ...(signature === undefined || signature.length === 0 ? {} : { signature }),
          }
        : trade,
    ),
  }));
  logger.warn('trade.submission_status_unknown', {
    submissionId: id,
    traceId: traceId ?? null,
    code,
    hasSignature: signature !== undefined && signature.length > 0,
  });
}
